/**
 * Единая точка определения среды: Telegram Mini App / Max WebApp / обычный браузер.
 * Приоритет: явные URL Telegram → пользователь Telegram → пользователь Max → UA → наличие SDK.
 */

export type MessengerKind = "telegram" | "max" | "web";

export interface TelegramRuntimeUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
  is_premium?: boolean;
}

export interface MaxRuntimeUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

export interface RuntimeMessengerSnapshot {
  kind: MessengerKind;
  userId: number | null;
  telegramUser: TelegramRuntimeUser | null;
  maxUser: MaxRuntimeUser | null;
  /** Откуда взялось решение (отладка) */
  source: string;
}

const defaultWeb: RuntimeMessengerSnapshot = {
  kind: "web",
  userId: null,
  telegramUser: null,
  maxUser: null,
  source: "none",
};

export function hasTelegramWebAppUrlParams(): boolean {
  if (typeof window === "undefined") return false;
  const search = new URLSearchParams(window.location.search);
  const hash = window.location.hash || "";
  return (
    search.has("tgWebAppData") ||
    search.has("tgWebAppPlatform") ||
    hash.includes("tgWebAppData=")
  );
}

/**
 * Разрешить среду по текущему window (вызывать только в браузере).
 */
export function resolveRuntimeMessenger(): RuntimeMessengerSnapshot {
  if (typeof window === "undefined") {
    return { ...defaultWeb, source: "ssr" };
  }

  const search = new URLSearchParams(window.location.search);
  const platform = search.get("platform");

  if (platform === "telegram") {
    const u = window.Telegram?.WebApp?.initDataUnsafe?.user ?? null;
    return {
      kind: "telegram",
      userId: u?.id ?? null,
      telegramUser: u ?? null,
      maxUser: null,
      source: "query:platform=telegram",
    };
  }

  if (platform === "max") {
    const u = window.WebApp?.initDataUnsafe?.user ?? null;
    const id = u?.id ?? null;
    return {
      kind: "max",
      userId: id,
      telegramUser: null,
      maxUser: u ?? null,
      source: "query:platform=max",
    };
  }

  if (hasTelegramWebAppUrlParams()) {
    const u = window.Telegram?.WebApp?.initDataUnsafe?.user ?? null;
    return {
      kind: "telegram",
      userId: u?.id ?? null,
      telegramUser: u ?? null,
      maxUser: null,
      source: "url:tgWebApp",
    };
  }

  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user ?? null;
  if (tgUser) {
    return {
      kind: "telegram",
      userId: tgUser.id,
      telegramUser: tgUser,
      maxUser: null,
      source: "initData:telegram",
    };
  }

  const maxUser = window.WebApp?.initDataUnsafe?.user ?? null;
  if (maxUser && typeof maxUser.id === "number") {
    return {
      kind: "max",
      userId: maxUser.id,
      telegramUser: null,
      maxUser,
      source: "initData:max",
    };
  }

  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes("telegram")) {
    const u = window.Telegram?.WebApp?.initDataUnsafe?.user ?? null;
    return {
      kind: "telegram",
      userId: u?.id ?? null,
      telegramUser: u ?? null,
      maxUser: null,
      source: "ua:telegram",
    };
  }

  if (ua.includes("max")) {
    const u = window.WebApp?.initDataUnsafe?.user ?? null;
    return {
      kind: "max",
      userId: u?.id ?? null,
      telegramUser: null,
      maxUser: u ?? null,
      source: "ua:max",
    };
  }

  if (window.Telegram?.WebApp) {
    const u = window.Telegram?.WebApp?.initDataUnsafe?.user ?? null;
    return {
      kind: "telegram",
      userId: u?.id ?? null,
      telegramUser: u ?? null,
      maxUser: null,
      source: "sdk:telegram",
    };
  }

  // Не считаем среду Max только по факту наличия window.WebApp:
  // в обычном браузере объект может быть подмешан сторонним SDK/скриптом.
  // Для Max требуется хотя бы API openLink/requestContact.
  if (
    window.WebApp &&
    (typeof window.WebApp.openLink === "function" ||
      typeof window.WebApp.requestContact === "function")
  ) {
    const u = window.WebApp?.initDataUnsafe?.user ?? null;
    return {
      kind: "max",
      userId: u?.id ?? null,
      telegramUser: null,
      maxUser: u ?? null,
      source: "sdk:max",
    };
  }

  return { ...defaultWeb, source: "none" };
}

/** Поля для Orders.php (актуальный снимок window). */
export function getMessengerInitData(): string {
  if (typeof window === "undefined") return "";
  const r = resolveRuntimeMessenger();
  if (r.kind === "telegram") {
    return window.Telegram?.WebApp?.initData ?? "";
  }
  if (r.kind === "max") {
    return window.WebApp?.initData ?? "";
  }
  return "";
}

/** Поля для Orders.php (актуальный снимок window). */
export function getOrdersMessengerPayload(): {
  messenger: MessengerKind;
  user_id: number | null;
  init_data: string;
} {
  const r = resolveRuntimeMessenger();
  return {
    messenger: r.kind,
    user_id: r.userId,
    init_data: getMessengerInitData(),
  };
}
